import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, input } from '@angular/core';
import { Router } from '@angular/router';

import { IConstant } from '@frontend/oxygen/shared-utils';
import { SpeculativeLinkDirective } from '@frontend/vanilla/features/speculative-link';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import * as _ from 'underscore';

import { ScrollOnceDirective } from '../../../core/directives/scroll-once-directive';
import { IGATrackingModel } from '../../../core/models/gtm.event.model';
import { ISwitcherConfig } from '../../../core/models/switcher-config.model';
import { FiltersService } from '../../../core/services/filters/filters.service';
import { GtmService } from '../../../core/services/gtm/gtm.service';
import { GtmTrackingService } from '../../../core/services/gtmTracking/gtm-tracking.service';
import { LocaleService } from '../../../core/services/locale/locale.service';
import { NavigationService } from '../../../core/services/navigation/navigation.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { GA_TRACKING, GOLF_CURRENT_TAB } from '../../constants/channel.constant';
import { ScrollableDirective } from '../../directives/scrollable.directive';
import { RoutingState } from '../../services/routingState/routing-state.service';
import { ITab } from '../tabsPanel/tabs-panel.model';

@Component({
    selector: 'switchers',
    templateUrl: 'switchers.component.html',
    imports: [NgClass, ScrollableDirective, ScrollOnceDirective, SpeculativeLinkDirective],
    host: {
        '[class]': 'hostClasses()',
        '[attr.data-crlat]': 'dataCrlat()',
    },
})
export class DefaultSwitchersComponent implements OnInit, OnDestroy, OnChanges {
    @Output() readonly switchAction?: EventEmitter<{ id: string; tab: ITab | {} }> = new EventEmitter();

    @Input() switchIdPropertyName?: string;
    @Input() type: string;
    @Input() switchers: ISwitcherConfig[] | ITab[] | any;
    @Input() filter: number | string;
    @Input() activeTab: ITab | IConstant;
    @Input() noPaddings: boolean;
    @Input() noMargin: boolean;
    @Input() detectGTMLocation?: string;
    @Input() preventRouteChange?: boolean;
    @Input() GATrackingModule?: string;
    @Input() GTMTrackingObj?: IGATrackingModel;
    @Input() isOverlay?: boolean = false;
    @Input() preventReload?: boolean;
    @Input() sportName?: string;
    @Input() preBuilt?: string;
    readonly maxTabsForEqualColumns: number = 4;
    executeOpenUrl: boolean = true;

    hostClasses = input<string>();
    dataCrlat = input<string>();

    constructor(
        private locale: LocaleService,
        public router: Router,
        private gtmTrackingService: GtmTrackingService,
        private navigationService: NavigationService,
        private gtmService: GtmService,
        protected filtersService: FiltersService,
        protected storage: StorageService,
        protected routingState: RoutingState,
        protected strategyProvider: RxStrategyProvider,
    ) {}

    getTabName(tab: ISwitcherConfig | ITab) {
        const translationName = (tab as ITab).title || tab.label || (tab as ISwitcherConfig).name;
        return translationName && translationName.match(/\./) ? this.locale.getString(translationName) : translationName;
    }

    ngOnInit() {
        this.type = this.type || 'regular';
        this.gtmTrackTabName();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.gtmTrackTabName();
        if (
            changes.activeTab &&
            changes.activeTab.currentValue &&
            changes.activeTab.currentValue.id &&
            changes.activeTab.currentValue.id.toString().includes('tab-')
        ) {
            this.gtmTrackingService.activeTabData = changes.activeTab;
        }
        if (this.sportName == 'golf') {
            if (
                changes.activeTab.currentValue &&
                changes.activeTab.previousValue &&
                changes.activeTab.currentValue.id !== changes.activeTab.previousValue.id &&
                GOLF_CURRENT_TAB.prevActiveTab !== changes.activeTab.currentValue.id
            ) {
                GOLF_CURRENT_TAB.activeTab = changes.activeTab.currentValue.id;
                const tab = changes.activeTab.currentValue;
                this.gtmTrackingService.sendGTMDataOnTabClick(tab, this.sportName);
            }
            GOLF_CURRENT_TAB.prevActiveTab = changes.activeTab.currentValue && changes.activeTab.currentValue.id;
        }
        const activeTabChange = changes.activeTab?.currentValue;
        const prevActiveTab = changes.activeTab?.previousValue;
        const isTabChanged = activeTabChange && (!prevActiveTab || activeTabChange.id !== prevActiveTab.id);
        if (isTabChanged && activeTabChange.label) {
            if (activeTabChange.url && activeTabChange.url.indexOf('/home') !== 0) {
                this.gtmTrackingService.sendGTMDataOnTabClick(activeTabChange, this.sportName);
            } else if (!activeTabChange.url && this.preBuilt) {
                this.gtmTrackingService.sendGTMDataOnTabClick(activeTabChange, this.sportName, this.preBuilt);
            }
        }
    }

    ngOnDestroy() {
        if (this.detectGTMLocation) {
            this.gtmTrackingService.clearLocation(this.detectGTMLocation);
        }
    }

    /**
     * Set Active Class
     * @param {ITab} tab
     * @returns {boolean}
     */
    isActive(tab: ISwitcherConfig | ITab, switcherIndex?: number): boolean {
        if (this.filter || this.filter === 0) {
            return this.filter === (tab as ISwitcherConfig)?.viewByFilters || (switcherIndex !== undefined && +this.filter === +switcherIndex);
        }
        return this.activeTab ? (tab as ITab).id === this.activeTab.id : !!(tab as ITab).selected;
    }

    /**
     * Tab Function
     * @param {ITab} tab
     * @param {MouseEvent} $event
     */
    clickFunction(tab: ISwitcherConfig | ITab, $event: MouseEvent, switchPos: number): void {
        this.executeOpenUrl = true;
        if (tab && tab.url && tab.url.includes('/home')) {
            this.router.navigateByUrl(tab.url);
            this.executeOpenUrl = false;
        }
        this.afterClick(tab, $event, switchPos);
    }

    afterClick(tab: ISwitcherConfig | ITab, $event: MouseEvent, switchPos: number) {
        $event.preventDefault();
        if (
            this.preventReload &&
            ((this.activeTab && this.activeTab.id === (tab as ITab).id) ||
                (Number(this.filter) >= 0 && this.switchers[this.filter].url === (tab as ITab).url))
        ) {
            return;
        }
        this.detectActiveTab(tab);

        if ((tab as ISwitcherConfig).onClick) {
            (tab as ISwitcherConfig).onClick((tab as ISwitcherConfig).viewByFilters);
            return;
        }

        this.strategyProvider
            .schedule(() => {
                if (this.switchAction?.observers.length) {
                    this.switchAction?.emit({ id: (tab as ITab)[(this.switchIdPropertyName as string) ?? ''], tab: tab as ITab });
                }

                if (
                    this.GTMTrackingObj &&
                    this.GTMTrackingObj.isHomePage &&
                    (this.GATrackingModule == GA_TRACKING.moduleRibbon.moduleName || this.GATrackingModule == GA_TRACKING.sportsRibbon.moduleName)
                ) {
                    this.gtmTracker({ ...tab, position: switchPos });
                }

                if ((tab as ITab).url && !this.preventRouteChange && this.executeOpenUrl) {
                    this.navigationService.openUrl((tab as ITab).url, true, true);
                }

                // new tracking comes here
                const gtmData = {
                    'event': 'Event.Tracking',
                    'component.CategoryEvent': 'betslip',
                    'component.LabelEvent': 'my bets',
                    'component.ActionEvent': 'click',
                    'component.PositionEvent': this.activeTab.name || 'Open',
                    'component.LocationEvent': 'my bets',
                    'component.EventDetails': tab.name,
                    'component.URLClicked': tab.url,
                };
                this.gtmService.push(gtmData.event, gtmData);
            })
            .subscribe();
    }

    /**
     * GA Tracking click actions
     * @returns void
     */
    gtmTracker(switcher: any): void {
        const trackingObj = {
            eventAction: switcher.title,
            eventCategory: GA_TRACKING.moduleRibbon.eventCategory,
            eventLabel: switcher.url,
            position: switcher.position,
        };
        this.gtmService.push('trackEvent', trackingObj);
    }

    isAutoSizable() {
        const displayedSwitchersCount = _.filter(this.switchers, (switcher: ISwitcherConfig | ITab) => !(switcher as ITab).hidden).length;
        return displayedSwitchersCount > this.maxTabsForEqualColumns;
    }

    detectActiveTab(tab: ISwitcherConfig | ITab | any): void {
        if (this.isOverlay) {
            this.switchers.forEach((switcher: ITab) => {
                switcher.selected = switcher.viewByFilters === tab.viewByFilters ? true : false;
            });
        }

        this.strategyProvider
            .schedule(() => {
                if (this.detectGTMLocation && tab) {
                    this.gtmTrackingService.setLocation(this.getTabName(tab), this.detectGTMLocation);
                }
            })
            .subscribe();
    }

    private gtmTrackTabName(): void {
        let activeTab;

        this.filterLinks();
        if (this.type === 'links') {
            const filter = this.filter || 0;
            activeTab = this.switchers[filter] || null;
        } else {
            activeTab = _.find(this.switchers, (tab: ITab, index) => this.isActive(tab, Number(index)));
        }
        activeTab?.id && this.storage.set('activetab', activeTab);
        if (!this.detectGTMLocation) {
            return;
        }
        this.detectActiveTab(activeTab);
    }

    filterLinks() {
        this.switchers?.map((tab: any) => {
            if (tab.url && tab.url.includes('racingsuperseries')) {
                this.filtersService.filterLinkforRSS(tab.url).subscribe((data) => {
                    tab.url = data;
                });
            }
        });
    }

    bybCheck(tab: ISwitcherConfig | ITab): boolean {
        return this.getTabName(tab).toUpperCase() === 'BET BUILDER' && tab.url?.indexOf('/home') !== 0;
    }

    isHomePage(tab: string): boolean {
        return this.routingState.isHomePage(tab);
    }
}
